import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type ApiMetricsEndpoint } from '../../api/adminApi';

type TimeRange = 1 | 6 | 24 | 168;
const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
];

export function ApiMetrics() {
  const [hours, setHours] = useState<TimeRange>(24);
  const [expandedUrl, setExpandedUrl] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['api-metrics', hours],
    queryFn: () => adminApi.getApiMetrics(hours),
    refetchInterval: 60_000,
  });

  const totalCalls = data?.endpoints.reduce((s, e) => s + e.callCount, 0) ?? 0;
  const totalRU = data?.endpoints.reduce((s, e) => s + e.totalRU, 0) ?? 0;
  const avgRUPerCall = totalCalls ? Math.round((totalRU / totalCalls) * 100) / 100 : 0;

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>API Metrics</h2>
        <p className="section-description">
          Per-endpoint CosmosDB RU consumption and latency from DAL metrics.
        </p>
      </div>

      {/* Time range selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TIME_RANGES.map(r => (
          <button
            key={r.value}
            className={`settings-tab ${hours === r.value ? 'active' : ''}`}
            onClick={() => setHours(r.value)}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="loading">Loading metrics...</div>}
      {error && <div className="error">Error: {(error as Error).message}</div>}

      {data && (
        <>
          {/* Summary row */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
            <SummaryCard label="Total API Calls" value={totalCalls.toLocaleString()} />
            <SummaryCard label="Total RU" value={totalRU.toLocaleString()} />
            <SummaryCard label="Avg RU / Call" value={avgRUPerCall.toLocaleString()} />
            <SummaryCard
              label="Throttles (429)"
              value={(data.totalThrottles ?? 0).toLocaleString()}
              alert={(data.totalThrottles ?? 0) > 0}
            />
            <SummaryCard
              label="Absorbed 429-retries"
              value={(data.totalAbsorbedThrottleRetries ?? 0).toLocaleString()}
              alert={(data.totalAbsorbedThrottleRetries ?? 0) > 0}
            />
          </div>

          {/* Endpoints table */}
          {data.endpoints.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)' }}>No DAL metrics found for this period.</p>
          ) : (
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th>URL</th>
                  <th style={{ textAlign: 'right' }}>Calls</th>
                  <th style={{ textAlign: 'right' }}>Avg RU</th>
                  <th style={{ textAlign: 'right' }}>Avg Latency</th>
                  <th style={{ textAlign: 'right' }} title="Hard CosmosDB 429s (SDK retries exhausted)">429</th>
                  <th style={{ textAlign: 'right' }}>Total RU</th>
                </tr>
              </thead>
              <tbody>
                {data.endpoints.map(ep => (
                  <EndpointRow
                    key={ep.url}
                    endpoint={ep}
                    expanded={expandedUrl === ep.url}
                    onToggle={() => setExpandedUrl(expandedUrl === ep.url ? null : ep.url)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{
      background: 'var(--bg-tertiary)',
      borderRadius: 8,
      padding: '12px 20px',
      minWidth: 140,
      ...(alert ? { boxShadow: 'inset 0 0 0 1px var(--color-error)' } : {}),
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: alert ? 'var(--color-error)' : 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function EndpointRow({
  endpoint,
  expanded,
  onToggle,
}: {
  endpoint: ApiMetricsEndpoint;
  expanded: boolean;
  onToggle: () => void;
}) {
  const containers = Object.entries(endpoint.containers);
  const hasDetail = containers.length > 0 || !!endpoint.sdk;
  const throttles = endpoint.throttleCount ?? 0;

  return (
    <>
      <tr
        onClick={hasDetail ? onToggle : undefined}
        style={{ cursor: hasDetail ? 'pointer' : 'default' }}
      >
        <td style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
          {hasDetail ? (expanded ? '▾' : '▸') : ''}
        </td>
        <td><code style={{ fontSize: 13 }}>{endpoint.url}</code></td>
        <td style={{ textAlign: 'right' }}>{endpoint.callCount}</td>
        <td style={{ textAlign: 'right' }}>{endpoint.avgRU}</td>
        <td style={{ textAlign: 'right' }}>{endpoint.avgLatencyMs}ms</td>
        <td style={{ textAlign: 'right', fontWeight: throttles > 0 ? 600 : 400, color: throttles > 0 ? 'var(--color-error)' : 'var(--text-tertiary)' }}>
          {throttles || '—'}
        </td>
        <td style={{ textAlign: 'right', fontWeight: 600 }}>{endpoint.totalRU}</td>
      </tr>
      {expanded && endpoint.sdk && (
        <tr style={{ background: 'var(--bg-tertiary)', fontSize: 13 }}>
          <td></td>
          <td style={{ paddingLeft: 32, color: 'var(--text-secondary)' }} colSpan={6}>
            SDK diagnostics — absorbed 429-retries: <strong style={{ color: endpoint.sdk.throttledRetries > 0 ? 'var(--color-error)' : 'inherit' }}>{endpoint.sdk.throttledRetries}</strong>
            {' · '}total retries: {endpoint.sdk.retries}
            {' · '}metadata lookups: {endpoint.sdk.metadataLookups}
          </td>
        </tr>
      )}
      {expanded && containers.map(([name, cs]) => (
        <tr key={name} style={{ background: 'var(--bg-tertiary)', fontSize: 13 }}>
          <td></td>
          <td style={{ paddingLeft: 32, color: 'var(--text-secondary)' }}>{name}</td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{cs.opCount} ops</td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{cs.avgRU}</td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{cs.avgLatencyMs}ms</td>
          <td></td>
          <td></td>
        </tr>
      ))}
    </>
  );
}
